import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TipoLineaMaterial } from '@cmms/shared';
import { api, consulta, descargarArchivo } from '../servicios/api.js';
import { Aviso, Dialogo } from './piezas.js';

interface OpcionConsolidado {
  tipo: 'OT' | 'SOLICITUD';
  id: string;
  numero: number;
  titulo: string;
  sede: string | null;
  estado: string;
  lineas: number;
}

interface FilaConsolidado {
  tipo: 'OT' | 'SOLICITUD';
  id: string;
  numero: number;
  titulo: string;
  sede: string | null;
  nombre: string;
  tipoLinea: TipoLineaMaterial;
  cantidad: number;
  nota: string | null;
}

interface Total {
  nombre: string;
  tipoLinea: TipoLineaMaterial;
  cantidad: number;
}

const ETIQUETA_TIPO: Record<TipoLineaMaterial, string> = {
  HERRAMIENTA: 'Herramienta',
  MATERIAL: 'Material',
};

const SIN_SEDE = 'Sin sede';

/* Las tablas de resultados comparten el ancho de la columna de cantidad, para que queden en fila. */
const tablaResultado = { tableLayout: 'fixed' as const, width: '100%' };
const colCantidad = { width: 150 };

const clave = (o: { tipo: string; id: string }): string => `${o.tipo}:${o.id}`;

function etiquetaOrigen(o: { tipo: string; numero: number; titulo: string }): string {
  return `${o.tipo === 'OT' ? 'OT' : 'Solicitud'} #${o.numero} · ${o.titulo}`;
}

/** Suma las cantidades de un mismo material (mismo nombre y tipo) entre todas las OT y solicitudes. */
function sumarPorMaterial(filas: FilaConsolidado[]): Total[] {
  const mapa = new Map<string, Total>();
  for (const f of filas) {
    const llave = `${f.tipoLinea}:${f.nombre.trim().toLowerCase()}`;
    const actual = mapa.get(llave);
    if (actual) actual.cantidad += f.cantidad;
    else mapa.set(llave, { nombre: f.nombre, tipoLinea: f.tipoLinea, cantidad: f.cantidad });
  }
  return [...mapa.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/** Texto plano para pegar en un bloc de notas: una sección por OT o solicitud, y al final el total. */
function textoConsolidado(filas: FilaConsolidado[], elegidas: OpcionConsolidado[]): string {
  const bloques: string[] = [];
  for (const opcion of elegidas) {
    const lineas = filas.filter((f) => f.tipo === opcion.tipo && f.id === opcion.id);
    const cabecera = `${etiquetaOrigen(opcion)}${opcion.sede ? ` (${opcion.sede})` : ''}`;
    const cuerpo = lineas.length
      ? lineas.map((f) => `  - ${f.nombre}: ${f.cantidad}`)
      : ['  Sin materiales planificados'];
    bloques.push([cabecera, ...cuerpo].join('\n'));
  }
  const totales = sumarPorMaterial(filas);
  if (totales.length > 0) {
    bloques.push(['TOTAL A USAR', ...totales.map((t) => `  - ${t.nombre}: ${t.cantidad}`)].join('\n'));
  }
  return bloques.join('\n\n');
}

/**
 * Botón para las páginas de OT y solicitudes. Abre un diálogo donde eliges varias
 * OT y solicitudes (con su sede) y ves juntos los materiales y herramientas que
 * planificaste en cada una, con sus cantidades. Desde ahí se descarga en Excel o
 * se copia como texto. Solo lee: no cambia las listas de cada OT o solicitud.
 */
export function BotonConsolidadoMateriales({
  etiqueta = 'Consolidado de materiales',
  elegidas,
}: {
  etiqueta?: string;
  /**
   * Claves `OT:<id>` o `SOLICITUD:<id>` de las filas seleccionadas. Si vienen, el
   * diálogo muestra solo esas (sin selector) y el botón es para ellas.
   */
  elegidas?: string[];
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <button type="button" className="boton secundario pequeno" onClick={() => setAbierto(true)}>
        {etiqueta}
      </button>
      {abierto ? (
        <DialogoConsolidado
          alCerrar={() => setAbierto(false)}
          elegidasIniciales={elegidas ?? []}
          soloElegidas={elegidas !== undefined}
        />
      ) : null}
    </>
  );
}

function DialogoConsolidado({
  alCerrar,
  elegidasIniciales,
  soloElegidas,
}: {
  alCerrar: () => void;
  elegidasIniciales: string[];
  soloElegidas: boolean;
}): JSX.Element {
  const [elegidas, setElegidas] = useState<Set<string>>(() => new Set(elegidasIniciales));
  const [sede, setSede] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [viendoLista, setViendoLista] = useState(soloElegidas);
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: opciones = [], isLoading } = useQuery({
    queryKey: ['consolidado-materiales', 'opciones'],
    queryFn: () => api.get<OpcionConsolidado[]>('/listas-materiales/consolidado/opciones'),
  });

  const seleccionadas = opciones.filter((o) => elegidas.has(clave(o)));
  const ordenes = seleccionadas.filter((o) => o.tipo === 'OT').map((o) => o.id);
  const solicitudes = seleccionadas.filter((o) => o.tipo === 'SOLICITUD').map((o) => o.id);
  const parametros = { ordenes, solicitudes };

  const sedes = [...new Set(opciones.map((o) => o.sede ?? SIN_SEDE))].sort((a, b) => a.localeCompare(b, 'es'));
  const termino = busqueda.trim().toLowerCase();
  const visibles = opciones.filter(
    (o) =>
      (sede === '' || (o.sede ?? SIN_SEDE) === sede) &&
      (termino === '' || `${o.numero} ${o.titulo}`.toLowerCase().includes(termino)),
  );
  const todasVisibles = visibles.length > 0 && visibles.every((o) => elegidas.has(clave(o)));

  const { data: filas = [], isLoading: cargandoFilas, error } = useQuery({
    queryKey: ['consolidado-materiales', 'lista', ordenes, solicitudes],
    queryFn: () => api.get<FilaConsolidado[]>(`/listas-materiales/consolidado${consulta(parametros)}`),
    enabled: viendoLista && seleccionadas.length > 0,
  });

  const alternar = (o: OpcionConsolidado): void =>
    setElegidas((s) => {
      const siguiente = new Set(s);
      if (siguiente.has(clave(o))) siguiente.delete(clave(o));
      else siguiente.add(clave(o));
      return siguiente;
    });

  const alternarVisibles = (): void =>
    setElegidas((s) => {
      const siguiente = new Set(s);
      for (const o of visibles) {
        if (todasVisibles) siguiente.delete(clave(o));
        else siguiente.add(clave(o));
      }
      return siguiente;
    });

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(textoConsolidado(filas, seleccionadas));
      setAviso('Lista copiada. Ya puedes pegarla en el bloc de notas.');
    } catch {
      setAviso('No se pudo copiar. Intenta de nuevo.');
    }
  };

  const descargar = async (): Promise<void> => {
    try {
      await descargarArchivo(`/listas-materiales/consolidado/exportar/xlsx${consulta(parametros)}`);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo descargar el archivo.');
    }
  };

  return (
    <Dialogo
      titulo={viendoLista ? `Lista consolidada · ${seleccionadas.length} elegida(s)` : 'Consolidado de materiales'}
      ancho={860}
      alCerrar={alCerrar}
    >
      {aviso ? <Aviso>{aviso}</Aviso> : null}

      {!viendoLista ? (
        <>
          <p className="campo-nota">
            Elige OT y solicitudes para ver juntos los materiales y herramientas que planificaste en cada una, con sus
            cantidades.
          </p>
          <div className="rejilla-campos">
            <div className="campo">
              <label htmlFor="consolidado-sede">Sede</label>
              <select id="consolidado-sede" value={sede} onChange={(e) => setSede(e.target.value)}>
                <option value="">Todas las sedes</option>
                {sedes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="consolidado-buscar">Buscar por número o título</label>
              <input id="consolidado-buscar" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </div>
          </div>

          <div className="acciones-fila" style={{ margin: '12px 0' }}>
            <button type="button" className="boton secundario pequeno" onClick={alternarVisibles} disabled={visibles.length === 0}>
              {todasVisibles ? 'Deseleccionar visibles' : 'Seleccionar visibles'}
            </button>
            <button
              type="button"
              className="boton secundario pequeno"
              onClick={() => setElegidas(new Set())}
              disabled={elegidas.size === 0}
            >
              Limpiar selección
            </button>
            <button type="button" className="boton" onClick={() => setViendoLista(true)} disabled={seleccionadas.length === 0}>
              Ver lista consolidada ({seleccionadas.length})
            </button>
          </div>

          {isLoading ? (
            <p>Cargando…</p>
          ) : opciones.length === 0 ? (
            <p>Todavía no hay OT ni solicitudes.</p>
          ) : visibles.length === 0 ? (
            <p>Ninguna OT o solicitud coincide con el filtro.</p>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th style={{ width: 40 }} aria-label="Elegir" />
                    <th>Origen</th>
                    <th>Número</th>
                    <th>Título</th>
                    <th>Sede</th>
                    <th>Estado</th>
                    <th>Materiales</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((o) => (
                    <tr key={clave(o)}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Elegir ${etiquetaOrigen(o)}`}
                          checked={elegidas.has(clave(o))}
                          onChange={() => alternar(o)}
                        />
                      </td>
                      <td>{o.tipo === 'OT' ? 'OT' : 'Solicitud'}</td>
                      <td>#{o.numero}</td>
                      <td>{o.titulo}</td>
                      <td>{o.sede ?? '—'}</td>
                      <td>{o.estado}</td>
                      <td>{o.lineas > 0 ? `${o.lineas} línea(s)` : 'Sin planificar'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          {error ? <Aviso tono="error">No se pudo cargar la lista.</Aviso> : null}
          {cargandoFilas ? <p>Cargando…</p> : null}
          {!cargandoFilas && !error ? (
            <>
              {seleccionadas.map((o) => {
                const lineas = filas.filter((f) => f.tipo === o.tipo && f.id === o.id);
                return (
                  <div key={clave(o)} style={{ marginBottom: 18 }}>
                    <strong>{etiquetaOrigen(o)}</strong>
                    <p className="campo-nota" style={{ margin: '2px 0 6px' }}>
                      Sede: {o.sede ?? 'Sin sede'}
                    </p>
                    {lineas.length === 0 ? (
                      <p className="campo-nota">No tiene materiales planificados.</p>
                    ) : (
                      <table className="tabla" style={tablaResultado}>
                        <thead>
                          <tr>
                            <th>Material o herramienta</th>
                            <th style={colCantidad}>Cantidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineas.map((f, i) => (
                            <tr key={`${f.id}-${i}`}>
                              <td>{f.nombre}</td>
                              <td style={colCantidad}>{f.cantidad}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}

              {filas.length > 0 ? (
                <div style={{ marginBottom: 18 }}>
                  <strong>Total a usar</strong>
                  <table className="tabla" style={{ ...tablaResultado, marginTop: 6 }}>
                    <thead>
                      <tr>
                        <th>Material o herramienta</th>
                        <th style={colCantidad}>Cantidad total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sumarPorMaterial(filas).map((t) => (
                        <tr key={`${t.tipoLinea}:${t.nombre}`}>
                          <td>{t.nombre}</td>
                          <td style={colCantidad}>{t.cantidad}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          ) : null}

          <div className="acciones-fila" style={{ marginTop: 12 }}>
            {soloElegidas ? null : (
              <button type="button" className="boton secundario" onClick={() => setViendoLista(false)}>
                Volver a elegir
              </button>
            )}
            <button type="button" className="boton secundario" onClick={copiar} disabled={cargandoFilas || !!error}>
              Copiar como texto
            </button>
            <button type="button" className="boton" onClick={descargar} disabled={cargandoFilas || !!error}>
              Descargar Excel
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}
